import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { AnimatedUSDText } from '@/lib/components/Text/AnimatedUSDText';
import { useMatchingSharesAtPrice } from '@/lib/hooks/useMatchingSharesAtPrice';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { EMarketAction } from '@/lib/types/market-action';
import { Text } from '@/lib/ui-kit/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { calculateLimitFee } from '@/lib/utils/calculateFees';

export const LimitSellSummary = () => {
  const t = useTranslations('orderForm');
  const limitPrice = useOrderFormStore.use.limitPrice();
  const shares = useOrderFormStore.use.sharesCount();
  const outcomeId = useOrderFormStore.use.selectedOutcomeId();
  const intent = useOrderFormStore.use.intent();

  const limitPriceUsd = useMemo(() => {
    return limitPrice.div(100);
  }, [limitPrice]);

  const { fees } = useFees();

  // Shares the book fills right away — they are charged the taker rate.
  const matchingShares = useMatchingSharesAtPrice({
    outcomeId: outcomeId ?? '',
    intent,
    action: EMarketAction.SELL,
    price: limitPriceUsd,
  });

  // Net of the fee withheld on execution — taker on whatever crosses the book.
  const receivingAmount = useMemo(() => {
    const gross = limitPriceUsd.mul(shares);
    if (!fees) return gross;
    return gross.minus(
      calculateLimitFee(limitPriceUsd, shares, matchingShares, EMarketAction.SELL, fees),
    );
  }, [shares, limitPriceUsd, matchingShares, fees]);

  if (!fees) {
    return null; // Don't render if fees are not loaded yet
  }

  return (
    <Text variant="body-small" className="flex w-full items-center justify-between">
      <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
        <Text variant="body-small">{t('receivingAmount')}</Text>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <InfoIcon />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={8}
            className="!w-fit max-w-[280px] px-2 py-1.5 !text-left"
          >
            <Text variant="label-md">{t('receivingAmountTooltip')}</Text>
          </TooltipContent>
        </Tooltip>
      </span>
      <span className="text-v2-green">
        <AnimatedUSDText value={receivingAmount} alwaysShowDecimals />
      </span>
    </Text>
  );
};
